import { first } from 'rxjs';
import { Store } from '@ngrx/store';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Component, Input, OnInit, inject } from '@angular/core';
import { ITrainingStageData } from '../../services/training/training.type';
import { CurrentPlatform, TextSizes, TextType } from '../../enums';
import { UtilsService } from '../../services/utils.service';
import { selectCurrentPlatform } from '../../../data/store/selectors/platform.selectors';
import { AddBaseUrlPipe } from '../../pipe/add-baseUrl/add-base-url.pipe';

import { TextComponent } from '../text/text.component';
import { IonicModule } from '@ionic/angular';
import { GetFilePicturePipe } from '../../pipe/get-file-picture/get-file-picture.pipe';
import { PdfViewerModalComponent } from '../pdf-viewer-modal/pdf-viewer-modal.component';

@Component({
  selector: 'app-stage-card',
  templateUrl: './stage-card.component.html',
  styleUrls: ['./stage-card.component.scss'],
  imports: [TextComponent, IonicModule, GetFilePicturePipe, PdfViewerModalComponent],
})
export class StageCardComponent implements OnInit {
  private _domSanitizer = inject(DomSanitizer);
  private _utilsService = inject(UtilsService);
  private _store = inject(Store);
  private _addBaseUrl = inject(AddBaseUrlPipe);

  @Input() item?: Partial<ITrainingStageData>;

  @Input() isImageCentered?: boolean;

  public readonly Platform = CurrentPlatform;

  public readonly TextType = TextType;

  public readonly TextSizes = TextSizes;

  public currentPlatform: CurrentPlatform | null = null;

  public isVideoModalOpen = false;

  public isPdfModalOpen = false;

  public embedVideoUrl: SafeResourceUrl | null = null;

  public directVideoUrl: string | null = null;

  public pdfUrl: string | null = null;

  ngOnInit(): void {
    this._store
      .select(selectCurrentPlatform)
      .pipe(first())
      .subscribe((currentPlatform) => (this.currentPlatform = currentPlatform));
  }

  get subtext() {
    if (!this.item?.trainingSection || !this.item?.trainingStage) {
      return '';
    }

    return [this.item.trainingSection.name, this.item.trainingStage.name].join(' · ');
  }

  public async openLink(): Promise<void> {
    const isMobile = this.currentPlatform !== CurrentPlatform.desktop;

    if (this.item?.uploadedFile?.path) {
      const { path, name, mimeType } = this.item.uploadedFile;
      const fileName = name || this.item.name || '';

      if (isMobile && this._utilsService.isPdfFile(fileName, mimeType)) {
        this.pdfUrl = this._addBaseUrl.transform(path);
        this.isPdfModalOpen = true;
        return;
      }

      if (isMobile && this._utilsService.isVideoFile(fileName, mimeType)) {
        this.directVideoUrl = this._addBaseUrl.transform(path);
        this.embedVideoUrl = null;
        this.isVideoModalOpen = true;
        return;
      }

      this._openInBrowser(path);
      return;
    }

    const embedUrl = this._getEmbedVideoUrl();
    if (embedUrl) {
      this.embedVideoUrl = embedUrl;
      this.directVideoUrl = null;
      this.isVideoModalOpen = true;
      return;
    }

    if (this.item?.url) {
      this._openInBrowser(this.item.url, false);
    }
  }

  public closeVideoModal(): void {
    this.isVideoModalOpen = false;
    this.embedVideoUrl = null;
    this.directVideoUrl = null;
  }

  public closePdfModal(): void {
    this.isPdfModalOpen = false;
    this.pdfUrl = null;
  }

  private _openInBrowser = async (urlString: string, transform = true) => {
    await this._utilsService.openUrlInBrowser(urlString, transform);
  };

  private _getEmbedVideoUrl(): SafeResourceUrl | null {
    if (!this.item?.url) {
      return null;
    }

    if (this.item.url.includes('youtube')) {
      return this._domSanitizer.bypassSecurityTrustResourceUrl(
        `https://www.youtube.com/embed/${this._getCodeFromYouTubeUrl(
          this.item.url,
        )}?rel=0&autoplay=1&loop=0&showinfo=0`,
      );
    }

    if (this.item.url.includes('rutube')) {
      return this._domSanitizer.bypassSecurityTrustResourceUrl(
        `https://rutube.ru/play/embed/${this._getCodeFromRutubeUrl(this.item.url)}`,
      );
    }

    return null;
  }

  private _getCodeFromRutubeUrl = (urlString: string) => {
    const url = new URL(urlString);
    const urlWithoutQuery = urlString.split('?')[0];

    const securityParam = url.searchParams.get('p');

    let code = urlWithoutQuery
      .split('/')
      .filter((p) => !!p)
      .pop();

    if (securityParam) {
      code += `/?p=${securityParam}`;
    }

    return code;
  };

  private _getCodeFromYouTubeUrl = (urlString: string) => {
    return new URL(urlString).searchParams.get('v');
  };
}
